import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { CoverDropzone } from "../components/CoverDropzone";

const GENRES = ["Fantasy", "Thriller", "Romance", "Sci-Fi", "Non-Fiction", "Mystery", "Historical", "Young Adult", "Horror", "Literary Fiction"];

export default function EditCampaign() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/campaigns/${id}`)
      .then(({ data }) => {
        if (user && data.author_id !== user.id) { setNotFound(true); return; }
        setForm({
          title: data.title || "",
          pen_name: data.pen_name || "",
          synopsis: data.synopsis || "",
          genre: data.genre || "Fantasy",
          arc_format: data.arc_format || "both",
          copies_available: data.copies_available || 1,
          review_deadline: (data.review_deadline || "").slice(0, 10),
          cover_url: data.cover_url || "",
          requirements: data.requirements || "",
          status: data.status || "active",
        });
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(apiError(e));
      })
      .finally(() => setLoading(false));
  }, [id, user]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, copies_available: Number(form.copies_available) };
      const { data } = await api.put(`/campaigns/${id}`, payload);
      toast.success("Campaign updated");
      nav(`/campaigns/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this campaign and all applications? This cannot be undone.")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      nav("/author");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16" style={{ color: "var(--ink-muted)" }}>Loading…</div>;
  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <div className="paper-card p-10">
        <h1 className="rainbow-text text-3xl mb-3">Campaign not found</h1>
        <p className="text-sm mb-6" style={{ color: "var(--ink-soft)" }}>Either it was removed or you don't have access to edit it.</p>
        <Button className="btn-press btn-rainbow" onClick={() => nav("/author")}>Back to studio</Button>
      </div>
    </div>
  );

  const penOptions = [user?.display_name, ...(user?.pen_names || [])].filter(Boolean);
  // Ensure current pen_name is selectable even if not in list
  if (form.pen_name && !penOptions.includes(form.pen_name)) penOptions.push(form.pen_name);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2">Edit campaign</div>
      <h1 className="rainbow-text text-4xl tracking-tight mb-8">Update &amp; refresh</h1>

      <form onSubmit={save} className="paper-card p-6 space-y-5" data-testid="edit-campaign-form">
        <div>
          <Label htmlFor="title">Book title</Label>
          <Input id="title" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Pen name for this campaign</Label>
            <Select value={form.pen_name} onValueChange={(v) => set("pen_name", v)}>
              <SelectTrigger data-testid="edit-penname"><SelectValue placeholder="Select pen name" /></SelectTrigger>
              <SelectContent>
                {penOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Genre</Label>
            <Select value={form.genre} onValueChange={(v) => set("genre", v)}>
              <SelectTrigger data-testid="edit-genre"><SelectValue /></SelectTrigger>
              <SelectContent>{GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="synopsis">Synopsis</Label>
          <Textarea id="synopsis" required rows={6} value={form.synopsis} onChange={(e) => set("synopsis", e.target.value)} data-testid="edit-synopsis" />
        </div>

        <div>
          <Label>ARC format offered</Label>
          <RadioGroup value={form.arc_format} onValueChange={(v) => set("arc_format", v)} className="flex flex-wrap gap-4 mt-2" data-testid="edit-format">
            {[
              { v: "ebook", label: "Ebook only" },
              { v: "physical", label: "Physical only" },
              { v: "both", label: "Ebook + Physical" },
            ].map((o) => (
              <div key={o.v} className="flex items-center gap-2">
                <RadioGroupItem value={o.v} id={`ef-${o.v}`} data-testid={`edit-format-${o.v}`} />
                <Label htmlFor={`ef-${o.v}`}>{o.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="copies">Copies available</Label>
            <Input id="copies" type="number" min={1} max={10000} required value={form.copies_available} onChange={(e) => set("copies_available", e.target.value)} data-testid="edit-copies" />
          </div>
          <div>
            <Label htmlFor="deadline">Review deadline</Label>
            <Input id="deadline" type="date" required value={form.review_deadline} onChange={(e) => set("review_deadline", e.target.value)} data-testid="edit-deadline" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active (accepting readers)</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Book cover</Label>
          <CoverDropzone value={form.cover_url} onChange={(v) => set("cover_url", v)} testId="edit-cover-dropzone" />
          <div className="flex items-center gap-2 mt-2">
            <Input type="url" placeholder="…or paste a cover URL" value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} data-testid="edit-cover" />
          </div>
        </div>

        <div>
          <Label htmlFor="req">Reviewer requirements</Label>
          <Textarea id="req" rows={3} value={form.requirements} onChange={(e) => set("requirements", e.target.value)} data-testid="edit-requirements" />
        </div>

        <div className="flex gap-3 justify-between items-center">
          <Button type="button" variant="outline" onClick={remove} className="btn-press" style={{ borderColor: "rgba(255,59,59,0.4)", color: "#FF3B3B" }} data-testid="edit-delete">
            Delete campaign
          </Button>
          <div className="flex gap-3">
            <Button type="button" variant="ghost" onClick={() => nav("/author")} data-testid="edit-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="btn-press btn-rainbow" data-testid="edit-submit">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
